import React from "react";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { Country } from "../../api/services/countries/type";

interface CountryTableProps {
  countries: Country[];
  onEdit?: (country: Country) => void;
  onDelete?: (countryId: string) => void;
  onView?: (country: Country) => void;
  isLoading?: boolean;
}

export const CountryTable: React.FC<CountryTableProps> = ({
  countries,
  onEdit,
  onDelete,
  onView,
  isLoading = false,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);

  const handleMenuToggle = (countryId: string) => {
    setOpenMenuId(openMenuId === countryId ? null : countryId);
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading countries...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-visible">
      <table className="w-full">
        <thead className="bg-slate-700">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Country Name
            </th>
            <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
              Calling Code
            </th>
            <th className="px-6 py-4 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700">
          {countries.map((country) => (
            <tr
              key={country.id}
              className="hover:bg-slate-700 transition-colors cursor-pointer"
              onClick={() => onView?.(country)}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-green-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {country.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-white">
                      {country.name}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-slate-300 font-mono">
                  {country.callingCode}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="relative inline-block">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuToggle(country.id);
                    }}
                    className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-600 transition-colors"
                  >
                    <MoreVertical size={16} />
                  </button>

                  {openMenuId === country.id && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-700 rounded-md shadow-lg z-10 border border-slate-600">
                      <div className="py-1">
                        {onView && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(() => onView(country));
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white transition-colors"
                          >
                            <Eye size={16} className="mr-3" />
                            View States
                          </button>
                        )}
                        {onEdit && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(() => onEdit(country));
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white transition-colors"
                          >
                            <Edit size={16} className="mr-3" />
                            Edit Country
                          </button>
                        )}
                        {onDelete && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(() => onDelete(country.id));
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                          >
                            <Trash2 size={16} className="mr-3" />
                            Delete Country
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {countries.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No countries found</div>
        </div>
      )}

      {openMenuId && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
